import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Base from '../Base';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import Card from '../../components/elements/Card';
import Icon from '../../components/common/Icon';
import Back from '../../components/common/Back';
import NotFound from '../../components/common/NotFound';
import Button from '../../components/elements/Button';

const SurveyResults = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // The result comes from the submit response (router state), with a
  // sessionStorage fallback so a refresh doesn't lose it.
  let result = location.state || null;
  if (!result) {
    try {
      const stored = sessionStorage.getItem('lastQuizResult');
      result = stored ? JSON.parse(stored) : null;
    } catch (e) {
      result = null;
    }
  }

  if (!result) {
    return (
      <Base activeNav="home">
        <Card>
          <Back to="/event-surveys" />
          <Title>
            <Icon name="clipboard-list" className="me-2" style={{ fontSize: '24px' }} theme="primary" />
            {t('quizResult')}
          </Title>
        </Card>
        <NotFound translationKey="errorLoading" />
      </Base>
    );
  }

  const earned = result.earned_points || 0;
  const total = result.total_points || 0;
  const completionPoints = result.completion_points || 0;
  const questions = Array.isArray(result.questions) ? result.questions : [];
  const awarded = result.awarded_to_team === true;
  const isScored = total > 0;
  const hasReward = isScored || completionPoints > 0;

  return (
    <Base activeNav="home">
      {/* Score header */}
      <Card className="mb-3">
        <Back to="/event-surveys" />
        <div className="d-flex flex-column align-items-center text-center">
          <div className="mb-3 mt-2">
            <Icon name="party-horn" theme="primary" style={{ fontSize: '3rem' }} />
          </div>
          <h4 className="mb-2">{t('quizCompleted')}</h4>

          {isScored ? (
            <>
              <div
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  color: 'var(--primary-color)',
                }}
              >
                {earned} / {total}
              </div>
              <div className="text-secondary mt-1">{t('quizPointsScored')}</div>
            </>
          ) : completionPoints > 0 ? (
            <>
              <div
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  color: 'var(--primary-color)',
                }}
              >
                +{completionPoints}
              </div>
              <div className="text-secondary mt-1">{t('surveyCompletionBonus')}</div>
            </>
          ) : (
            <div className="text-secondary mt-1">{t('quizThanksNoScore')}</div>
          )}

          {/* When the quiz is scored, surface the completion bonus separately */}
          {isScored && completionPoints > 0 && (
            <div className="mt-2">
              <span className="points-chip points-chip--earned">
                <Icon name="star" />
                +{completionPoints} {t('surveyCompletionBonus')}
              </span>
            </div>
          )}
        </div>
      </Card>

      {/* Who got the points (only one teammate counts) */}
      {hasReward && (
        <div style={{ fontSize: '13px' }} className="text-muted mx-3 mb-3">
          <Icon
            name={awarded ? 'check-circle' : 'info'}
            theme="primary"
            className="me-1"
          />
          {awarded ? t('quizPointsAwardedToTeam') : t('quizPointsAlreadyEarnedByTeam')}
        </div>
      )}

      {/* Per-question breakdown — points only, never the question text */}
      {isScored && questions.length > 0 && (
        <Card className="mb-3">
          <Title heading="5" my="0" className="mb-2">
            {t('quizBreakdown')}
          </Title>
          <div className="rules-table">
            <table className="table table-borderless rules-table__table">
              <thead>
                <tr>
                  <th>{t('question')}</th>
                  <th>{t('points')}</th>
                </tr>
              </thead>
              <tbody>
                {questions.map((q) => (
                  <tr key={q.order}>
                    <td>
                      <strong>
                        {t('question')} {q.order}
                      </strong>
                    </td>
                    <td>
                      <span
                        className={`points-chip ${q.points_earned > 0 ? 'points-chip--earned' : ''}`}
                        style={{ marginTop: 0 }}
                      >
                        <Icon name={q.points_earned > 0 ? 'star' : 'circle'} />
                        {q.points_earned} / {q.points_possible}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="mx-3">
        <Button onClick={() => navigate('/event-surveys')}>{t('backToQuizzes')}</Button>
      </div>
    </Base>
  );
};

export default SurveyResults;
